import React, { useEffect, useState } from "react";
import { apiClient } from "../lib/api";
import { fmtInr } from "../lib/format";

const SECTOR_COLORS = [
  "#1748C4", "#0E1B33", "#0E7490", "#248750", "#7C3AED",
  "#2563EB", "#C22121", "#5B6579", "#854D0E", "#65A30D",
  "#9333EA", "#0F766E", "#BE185D", "#A16207", "#1E3A8A",
];

const CAP_COLORS = {
  "Large Cap": "#1748C4",
  "Mid Cap": "#0E7490",
  "Small Cap": "#DB891C",
  "Unknown": "#94A3B8",
};

function StackedBar({ items, total, colorFor }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="flex h-6 w-full bg-secondary overflow-hidden border border-subtle">
      {items.map((it, i) => {
        const pct = total > 0 ? (it.value / total) * 100 : 0;
        if (pct < 0.5) return null;
        return (
          <div
            key={it.label}
            style={{ width: `${pct}%`, background: colorFor(it.label, i) }}
            title={`${it.label}: ${it.weight}%`}
          />
        );
      })}
    </div>
  );
}

function Breakdown({ title, items, colorFor, testId }) {
  const total = items.reduce((acc, it) => acc + it.value, 0);
  return (
    <div className="border border-subtle bg-card p-5" data-testid={testId}>
      <div className="text-[11px] uppercase-track font-semibold text-muted-foreground mb-3">
        {title}
      </div>
      <StackedBar items={items} total={total} colorFor={colorFor} />
      <div className="mt-4 space-y-2">
        {items.map((it, i) => (
          <div key={it.label} className="flex items-center text-sm">
            <span
              className="inline-block h-2.5 w-2.5 mr-2.5 shrink-0"
              style={{ background: colorFor(it.label, i) }}
            />
            <span className="truncate flex-1">{it.label}</span>
            <span className="font-mono tabular text-lime ml-3">{it.weight.toFixed(1)}%</span>
            <span className="font-mono tabular text-muted-foreground ml-4 hidden sm:inline">
              {fmtInr(it.value)}
            </span>
          </div>
        ))}
        {items.length === 0 ? (
          <div className="text-xs text-muted-foreground">No data yet.</div>
        ) : null}
      </div>
    </div>
  );
}

export default function DiversificationSection({ scope, clientId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const url = scope === "advisor"
      ? `/advisor/clients/${clientId}/diversification`
      : `/client/diversification`;
    setLoading(true);
    apiClient
      .get(url)
      .then((r) => alive && setData(r.data))
      .catch(() => alive && setData(null))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [scope, clientId]);

  if (loading) return <div className="text-sm text-muted-foreground">Computing diversification…</div>;
  if (!data) return <div className="text-sm text-muted-foreground">No holdings to analyze.</div>;
  if ((data.holdings_meta || []).length === 0) {
    return (
      <div className="border border-subtle p-10 text-center text-sm text-muted-foreground bg-card">
        No open holdings yet. Diversification will appear once positions are recorded.
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="diversification-section">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Breakdown
          title="By market cap"
          items={data.by_market_cap}
          colorFor={(label) => CAP_COLORS[label] || "#94A3B8"}
          testId="diversification-cap"
        />
        <Breakdown
          title="By sector"
          items={data.by_sector}
          colorFor={(_, i) => SECTOR_COLORS[i % SECTOR_COLORS.length]}
          testId="diversification-sector"
        />
      </div>

      <Breakdown
        title="By industry"
        items={data.by_industry}
        colorFor={(_, i) => SECTOR_COLORS[i % SECTOR_COLORS.length]}
        testId="diversification-industry"
      />

      <div className="border border-subtle bg-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="diversification-holdings-table">
          <thead className="bg-secondary border-b border-subtle">
            <tr className="text-[10px] uppercase-track text-muted-foreground">
              <th className="text-left p-3 font-medium">Symbol</th>
              <th className="text-left p-3 font-medium">Sector</th>
              <th className="text-left p-3 font-medium">Industry</th>
              <th className="text-left p-3 font-medium">Cap</th>
              <th className="text-right p-3 font-medium">Value</th>
              <th className="text-right p-3 font-medium">Weight</th>
            </tr>
          </thead>
          <tbody>
            {data.holdings_meta.map((h) => (
              <tr key={h.symbol} className="border-b border-subtle hover:bg-secondary">
                <td className="p-3 font-mono">{h.symbol}</td>
                <td className="p-3">{h.sector}</td>
                <td className="p-3 text-muted-foreground">{h.industry}</td>
                <td className="p-3">
                  <span className="text-[10px] px-1.5 py-0.5 bg-gold-soft font-mono">
                    {h.market_cap_category}
                  </span>
                </td>
                <td className="p-3 text-right font-mono tabular">{fmtInr(h.value)}</td>
                <td className="p-3 text-right font-mono tabular">{h.weight.toFixed(2)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
